'use client'
// import Link from 'next/link'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
// import { useCopyText } from '../context'
import { motion, useInView } from 'motion/react'
import { mq, vw, getP30, getP15 } from '@/styles'
import { Figure } from '../Figure'
import { LOCALE } from '@/utils'
import { ConditionalWrapper } from '../ConditionalWrapper'
import { TypeLang } from '@/schema'
import { VideoLocal } from '../VideoLocal'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { useDeviceType } from '@/hooks'

const hardcodedSlug = {
  es: {
    hellomamma: 'https://hellomamma.es', // B2C headless ecommerce / ver
    gigistudios: 'https://gigistudios.com/es/ESP', // B2C + B2B headless ecommerce / ver
    tria: 'https://tria.mobles114.com', // Furniture system design icon configurator / ver
    safeguru: 'https://safeguru.com', // B2C + B2B headless ecommerce / ver
    inditex: 'https://www.massimodutti.com/es', // Massimo Dutti Apps and website / ver
    aicam: 'https://aicam.studio/es',
    mammafiore: 'https://mammafiore.es',
    losiento: 'https://losiento.studio',
    sbfilms: 'https://sbfilms.com' // S/B Films corporate site / ver
  },
  en: {
    hellomamma: 'https://hellomamma.co.uk', // B2C headless ecommerce / view
    gigistudios: 'https://gigistudios.com/en/US', // B2C + B2B headless ecommerce / view
    tria: 'https://tria.mobles114.com', // Furniture system design icon configurator / view
    safeguru: 'https://safeguru.co.uk', // B2C + B2B headless ecommerce / view
    inditex: 'https://www.massimodutti.com',
    aicam: 'https://aicam.studio',
    mammafiore: 'https://mammafiore.co.uk',
    losiento: 'https://losiento.studio',
    sbfilms: 'https://sbfilms.com' // S/B Films corporate site / ver
  }
}

const getSlug = (clientKey: string, locale?: TypeLang) => {
  return hardcodedSlug[locale ?? LOCALE][clientKey]
}

const getMediaData = (media) => ({
  type: 'image',
  alt: media?.alt,
  m: {
    src: media?.default?.['2x']?.regular,
    size: media?.size,
  }
})

const VIEW_MORE_TRANSLATION = {
  es: 'Ver más',
  en: 'View more'
}

type Props = {
  data: {
    client: string
    logo?: {
      type: string
      alt: string
      url: string
    }
    image?: string
    media?: any
    video: any
    name: string
    linkDisabled?: boolean
    comingSoon?: string
		clientKey?: string
  }
}
export const Card:FC<Props> = memo(({ data }) => {
  const { clientKey, client, logo, image, media, video, name, linkDisabled, comingSoon } = data
  const { lang }  = useParams() as { lang: TypeLang }
  const [playVideo, setPlayVideo] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref)
  const { isTouchDevice } = useDeviceType()

  useEffect(() => {
    if (!isTouchDevice) return
    console.log('isTouchDevice', isTouchDevice)
    setPlayVideo(isInView)

    // cleanup function
    return () => {
      setPlayVideo(false)
    }
  }, [isTouchDevice, isInView])


  if(!data) {
    console.warn('No data for <Card />')
    return null
  }

  return (
    <Article ref={ref} onMouseOver={() => setPlayVideo(true)} onMouseLeave={() => setPlayVideo(false)} $playVideo={playVideo}>
      {media ?
        <Figure className='img' media={getMediaData(media)} lazy={false} />
        :
        <figure>
          {image ?
            // eslint-disable-next-line @next/next/no-img-element
            <img className='img' src={image} alt='Card image' />
            :
            <div className='img' />
          }
        </figure>
      }
      {video && <VideoLocal data={video} play={playVideo} setVideoReady={setPlayVideo} />}
      <ConditionalWrapper condition={(getSlug(clientKey, lang) && !linkDisabled)} wrapperTrue={(children) => <a className='link' href={getSlug(client, lang)} rel='noreferrer' target='_blank'>{children}</a>} wrapperFalse={children => <div className='link'>{children}</div>}>
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          {logo ? <img className='logo' src={logo.url} alt={logo.alt} /> : <div className='logo'>{client}</div>}
          <div className='text-wrapper'>
            <h3><span className='srt-only'>{client}</span>{name}</h3>
            <span className='span'>{!linkDisabled ? VIEW_MORE_TRANSLATION[lang || LOCALE] : comingSoon ?? ''}</span>
          </div>
        </>
      </ConditionalWrapper>
    </Article>
  )
})

const Article = styled(motion.article) <{ $playVideo?: boolean }>`
  border-radius: ${vw(6.96, 'mobile')};
  height: ${vw(454, 'mobile')};
  overflow: hidden;
  position: relative;
  width: ${vw(289, 'mobile')};

  ${mq.greaterThan('tablet')} {
    border-radius: ${vw(10, 'desktop')};
    height: ${vw(650, 'desktop')};
    transition: transform 700ms cubic-bezier(0.12, 0.07, 0.2, 1), box-shadow 700ms cubic-bezier(.25, .1, .25, 1);
    width: ${vw(414, 'desktop')};

    &:hover {
      transform: scale(1.03);
      box-shadow: 0 ${vw(35, 'desktop')} ${vw(45, 'desktop')}  rgba(0, 0, 0, .2);

      .img {
        transform: scale(1);
      }

      .link {
        transform: scale(0.97);

        .logo {
          transform: scale(1.03);
        }

        h3 {
          transform: translateY(${vw(-45, 'desktop')});
        }

        .span {
          opacity: 1;
          transform: translateY(${vw(-15, 'desktop')});
        }
      }
    }
  }

  div.link {
    cursor: auto;
  }

  video {
    bottom: 0;
    height: 100%;
    left: -2px;
    object-fit: cover;
    position: absolute;
    height: 100%;
    right: 0;
    top: 0;
    width: 100%;
    width: calc(100% + 4px);
    z-index: 3;
  }

  .link,
  a {
    align-items: flex-start;
    color: white;
    display: flex;
    flex-direction: column;
    height: ${vw(454, 'mobile')};
    width: ${vw(289, 'mobile')};
    justify-content: space-between;
    padding: ${vw(29, 'mobile')};
    position: relative;
    text-decoration: none;
    z-index: 5;

    ${mq.greaterThan('tablet')} {
      transition: transform 700ms cubic-bezier(0.12, 0.07, 0.2, 1);
      height: ${vw(650, 'desktop')};
      width: ${vw(414, 'desktop')};
      padding: ${vw(40, 'desktop')};
    }
  }

  .logo {
    font-weight: bold;
    max-height: 30px;
		max-width: 210px;
    transition: 150ms transform cubic-bezier(0.12, 0.07, 0.2, 1);
    width: auto;
    z-index: 5;
  }

  .img {
    background-color: var(--color-text);
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: ${({ $playVideo }) => $playVideo ? 0 : 1 };
    transform: scale(${({ $playVideo }) => $playVideo ? 1 : 1.1 });
    transition: 300ms opacity linear, transform 700ms cubic-bezier(0.12, 0.07, 0.2, 1);

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  .text-wrapper {
    position: relative;
    z-index: 5;
  }

  h3 {
    ${getP30()};
    text-align: left;
    transform: translateY(${vw(-45, 'mobile')});
    transition: transform 500ms cubic-bezier(.25, .1, .25, 1);

    ${mq.greaterThan('tablet')} {
      transform: translateY(0);
    }
  }

  .span {
    ${getP15()};
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    transform: translateY(${vw(-15, 'mobile')});
    transition: transform 500ms cubic-bezier(.25, .1, .25, 1), opacity 500ms cubic-bezier(.25, .1, .25, 1);
    z-index: 5;

    ${mq.greaterThan('tablet')} {
      opacity: 0;
      transform: translateY(0);
    }
  }
`
